import { useRef } from 'react';
import ExternalPhoneInput, {
    isValidPhoneNumber,
    parsePhoneNumber,
} from 'react-phone-number-input';

import flags from 'react-phone-number-input/flags';
import 'react-phone-number-input/style.css';
import styled from 'styled-components';

import { ErrorText, StyledText, textStyles } from '../atoms/Texts';

import { Box } from '../atoms';
import { colors } from '../utils/colors';
import { SupportedLanguages } from '../utils/typesForCommonLib';
import { randomString } from '../utils/utils';

const getDefaultCountry = (lang?: SupportedLanguages) => {
    const locale = typeof window !== 'undefined' ? navigator.language : 'NO';

    if (lang === 'da') {
        return 'DK';
    } else if (lang === 'fi') {
        return 'FI';
    } else if (lang === 'no') {
        return 'NO';
    }

    switch (locale) {
        case 'sv-SE':
            return 'SE';
        case 'nb-NO':
            return 'NO';
        case 'fi-FI':
            return 'FI';
        case 'da-DK':
            return 'DK';
        default:
            return 'NO';
    }
};

type Props = {
    lang: SupportedLanguages;
    value: string;
    onChange: (newPhone?: string) => unknown;
    label: string;
    error?: string;
};

// Based on the countries here: https://support.iterable.com/hc/en-us/articles/17944393899284-Restricted-SMS-Content-and-Regions

export const excludedCountries = new Set([
    'AF', // Afghanistan
    'BD', // Bangladesh
    'CD', // Congo, the Democratic Republic of the
    'CI', // Côte d'Ivoire
    'CN', // China
    'CU', // Cuba
    'ET', // Ethiopia
    'GH', // Ghana
    'IR', // Iran, Islamic Republic of
    'IQ', // Iraq
    'KE', // Kenya
    'KP', // Korea, Democratic People's Republic of
    'LB', // Lebanon
    'LY', // Libya
    'ML', // Mali
    'MM', // Myanmar
    'NG', // Nigeria
    'NI', // Nicaragua
    'PK', // Pakistan
    'RU', // Russian Federation
    'SD', // Sudan
    'SO', // Somalia
    'SS', // South Sudan
    'SY', // Syrian Arab Republic
    'UA', // Ukraine
    'VE', // Venezuela
    'YE', // Yemen
    'ZW', // Zimbabwe
]);

export function validatePhoneNumber(phoneNumber: string) {
    const parsedNumber = parsePhoneNumber(phoneNumber);

    if (phoneNumber.length > 0 && !isValidPhoneNumber(phoneNumber)) {
        return 'invalid';
    } else if (
        phoneNumber.length > 0 &&
        (!parsedNumber?.country || excludedCountries.has(parsedNumber.country))
    ) {
        return 'countrycode';
    }
}

export function PhoneInput({ lang, value, onChange, label, error }: Props) {
    const id = useRef<string>(randomString(3));
    const errorRef = useRef<string>(randomString(3));
    return (
        <ContainerPhoneInputStyle gap="8" direction="column">
            <StyledText
                as="label"
                variant="label"
                weight="bold"
                htmlFor={id.current}
            >
                {label}
            </StyledText>
            <span lang="en">
                <ExternalPhoneInput
                    id={id.current}
                    flags={flags}
                    defaultCountry={getDefaultCountry(lang)}
                    value={value}
                    onChange={(res?: string) => {
                        onChange(res);
                    }}
                />
            </span>
            {error && (
                <ErrorText id={errorRef.current} variant="body">
                    {error}
                </ErrorText>
            )}
        </ContainerPhoneInputStyle>
    );
}

export const ContainerPhoneInputStyle = styled(Box)`
    .PhoneInput {
        display: flex;
        gap: 8px;
    }

    .PhoneInputCountry {
        margin: 0;
    }
    .PhoneInputCountrySelect:focus + .PhoneInputCountryIcon--border {
        box-shadow: none;
    }

    .PhoneInputCountryIcon--border {
        box-shadow: none;
    }

    .PhoneInputCountrySelect:focus
        + .PhoneInputCountryIcon
        + .PhoneInputCountrySelectArrow {
        color: ${colors.gray900};
    }

    .PhoneInputCountrySelectArrow {
        color: ${colors.gray900};
        opacity: 1;
    }

    .PhoneInputCountry,
    .PhoneInputInput {
        ${textStyles({ variant: 'body', weight: 'regular' })}
        ${(props) => props.theme.input.default}
        /* keep this order so that we get the correct color for the text */
        &:focus-within {
            ${(props) => props.theme.input.focus}
        }
        &:hover {
            ${(props) => props.theme.input.hover}
        }
    }
`;
